<template>

  <MembershipBannerBar v-if="!userStore.isFullMember()">
    Full members can import their data from <i>Letterboxd</i>.
  </MembershipBannerBar>

<!--  ceremony nav bar-->
  <ul ref="ceremonynavbar" class="nav fixed-top bg-body-tertiary ceremony-navbar" :class="{'showing-message-banner': !userStore.isFullMember(), 'hide-unless-hovering': $screenshotMode && $screenshotModeSettings.hoverToRevealCeremonyNavbar}">
    <div v-for="(idx) in numCeremonies" :key="idx">
      <li class="nav-item" :class="{ active: ((numCeremonies+1)-idx === ceremony), 'decade-edge': isDecadeEdge((numCeremonies+1)-idx) }">
        <a @click="setCeremony((numCeremonies+1)-idx)" class="nav-link" aria-current="page" href="#">
          <div class="nav-ceremony-num">
            {{getCeremonyOrdinalText((numCeremonies+1)-idx)}}<span v-if="showNeedsReviewIndicator(((numCeremonies+1)-idx))" class="ceremony-needs-review-dot">{{'\u{2022}'}}</span>
          </div>
          <div class="nav-year">{{getYearOfCeremony((numCeremonies+1)-idx)}}</div>
        </a>
      </li>
    </div>
  </ul>

  <!--  ceremony checklist -->
  <div class="container-fluid checklist-container" :class="{'showing-message-banner': !userStore.isFullMember()}">

    <LoadingSpinner v-if="filmList.length === 0" class="text-secondary" loading_text="loading" style="margin-top: 100px;"></LoadingSpinner>

    <div v-else class="card mol-center-card-width ceremony-card bg-body-tertiary" :class="{'showing-message-banner': !userStore.isFullMember()}">

      <!--  floating header -->
      <div v-if="showCeremonyOverlay" class="floating-ceremony-header fixed-top col-12" :class="{'showing-message-banner': !userStore.isFullMember(), 'floating-ceremony-header-screenshot-mode': $screenshotMode}">
        <span class="ceremony-text">{{ceremonyOrdinalText}} Ceremony • {{ceremonyDateText}}</span><span v-if="showNeedsReviewIndicator(ceremony)" class="ceremony-needs-review-dot">{{'\u{2022}'}}</span>
        <div ref="floatingPercentageStatRef" :class="{ 'percentage-stat': true, 'no-films-viewed': (officiallyNominatedFilmsViewed === 0) }">{{officiallyNominatedPercentageViewed}}% viewed<span v-if="shouldDisplayFilmPercentageFootnote">*</span></div>
      </div>


      <div class="card-header ceremony-card-header">

        <div class="ceremony-number-and-year">{{ceremonyOrdinalText}} Ceremony • {{ceremonyDateText}}<span v-if="showNeedsReviewIndicator(ceremony)" class="ceremony-needs-review-dot">{{'\u{2022}'}}</span></div>
        <div>Honoring achievements in</div>
        <div>{{ filmList.length }} films from {{ ceremonyYearsHonoredText(ceremony) }}</div>
        <div ref="percentageStatRef" :class="{ 'percentage-stat': true, 'no-films-viewed': (officiallyNominatedFilmsViewed === 0) }">{{officiallyNominatedPercentageViewed}}% viewed<span v-if="shouldDisplayFilmPercentageFootnote">*</span></div>

        <div class="group-by-nomination">
          <div class="form-check form-switch group-by-nomination-switch">
            <input class="form-check-input" type="checkbox" id="groupByNomination" v-model="showByCategory">
            <label class="form-check-label" for="groupByNomination">group by nomination</label>
          </div>
        </div>
      </div>

      <!--  films separated into category headers -->
      <div v-if="showByCategory" class="card-body card-body-category-list">
          <div v-for="(category, index) in categoryList" :key="category">
            <div class="category-title" :class="{'first-category': index===0}">
              <ColorThemeImg :src="`${$imagePathBase}nom_icon_light.svg`" :dark_theme_src="`${$imagePathBase}nom_icon_dark.svg`" alt="Logo" class="logo-image"/> <span v-html="categoryDisplayNameHtml(category)"></span>
            </div>

            <div v-for="(film, idxFilm) in filmsNominatedForCategory(category)" :key="`${category} - ${film.film_id}`">
              <hr v-if="idxFilm > 0" class="film-separator">
              <ChecklistFilm
                  class="film-card"
                  :film=film
                  :show_nominees_for_category="category"
                  :data-film-id=film.film_id
              />
            </div>
        </div>
      </div>

      <!--  just the films in a big list -->
      <div v-else class="card-body card-body-film-list">
        <div v-for="(film, index) in filmList" :key="film._id">
          <hr v-if="index > 0" class="film-separator">
          <ChecklistFilm class="film-card" :film=film :data-film-id=film._id></ChecklistFilm>
        </div>
      </div>


      <div v-if="shouldDisplayFilmPercentageFootnote" class="film-percentage-footnote">
        * Only includes films with "official" nominations.
      </div>

    </div>

  </div>

</template>


<script>

import axios from "axios";
import {Film} from "@/film";
import ChecklistFilm from "@/components/ChecklistFilm.vue";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import {nextTick} from "vue";
import {userStore} from "@/userStore";
import analytics from "@/analytics";

// eslint-disable-next-line no-unused-vars
import {EventBus} from "@/eventBus";
import FilmReviewedIndicator from "@/components/FilmReviewedIndicator.vue";
import ColorThemeImg from "@/components/ColorThemeImg.vue";
import MembershipBannerBar from "@/components/MembershipBannerBar.vue";

// date format object for ceremony date at top
const dateFormatObj = new Intl.DateTimeFormat('en-US', {
  timeZone: 'UTC',
  month: 'short',
  day: 'numeric',
  year: 'numeric'
});

export default {
  name: "ChecklistPage",
  // eslint-disable-next-line
  components: {MembershipBannerBar, ColorThemeImg, FilmReviewedIndicator, LoadingSpinner, ChecklistFilm},
  props: ['init_ceremony'],
  data() {
    return {
      CEREMONY_REVIEWING_ENABLED: true,
      CEREMONY_NEEDS_REVIEW_INDICATOR_ENABLED: true,
      ceremony: null,
      numCeremonies: 0,
      filmList: [],
      filmsReviewed: new Set(),
      ceremonyOverview: null,
      showCeremonyOverlay: false,
      showByCategory: false,
      axiosAbortcontroller: null,
    }
  },
  async mounted() {

    if ( this.$screenshotMode ) {
      this.installDebugHooks();
    }

    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant',
    });
    window.addEventListener("scroll", this.onScroll, true);


    EventBus.on('viewed-state-updated', this.filmViewedStateUpdated);

    this.axiosAbortcontroller = new AbortController();
    axios.get('/api/ceremony/overview', { signal: this.axiosAbortcontroller.signal } )

        .then((result) => {
          this.ceremonyOverview = result.data;

          // put most recent ceremonies first
          this.ceremonyOverview.sort((cer1, cer2) => {
            return (cer1.ceremony < cer2.ceremony) ? 1 : (cer1.ceremony > cer2.ceremony) ? -1 : 0;
          });

          this.numCeremonies = this.ceremonyOverview.length;

          // give it a tick to render before trying to scroll
          return nextTick();
        })

        .then(() => {
          const initial_ceremony = this.init_ceremony? Number(this.init_ceremony) : this.numCeremonies;

          const init_scroll_percent = 1.0 - (initial_ceremony / this.numCeremonies);
          const init_scroll_pixels = (this.$refs.ceremonynavbar.scrollWidth * init_scroll_percent) - 20;

          this.$refs.ceremonynavbar.scrollTo({
            top: 0,
            left: init_scroll_pixels,
            behavior: 'instant',
          });
          return this.setCeremony( ((initial_ceremony > 0) && (initial_ceremony < this.numCeremonies))? initial_ceremony : this.numCeremonies );
        })

        .catch((err) => {
          if ( err.code !== 'ERR_CANCELED') {
            analytics.notifyBugsnagOfError(err);
          }
        })

        .finally(() => {
          this.axiosAbortcontroller = null;
        });
  },
  updated() {
  },
  beforeUnmount() {
    EventBus.off('viewed-state-updated', this.filmViewedStateUpdated);

    window.removeEventListener("scroll", this.onScroll, true);
    window.debugScrollCeremonyNavbar = undefined;

    // if we're mid-download, cancel it
    if ( this.axiosAbortcontroller ) {
      this.axiosAbortcontroller.abort();
      this.axiosAbortcontroller = null;

      //console.log('request aborted');
    }
  },
  computed: {
    userStore() {
      return userStore;
    },

    ceremonyOrdinalText() {
      return Film.getCeremonyOrdinalText(this.ceremony);
    },
    ceremonyDateText() {
      return dateFormatObj.format(Film.getDateOfCeremony(this.ceremony));
    },
    categoryList() {
      return Film.getNominatedCategoriesForFilms(this.filmList).map(c => c.name);
    },

    officiallyNominatedFilms() {
      return this.filmList.filter(film => film.nominations.some(nom => {
        return ( !nom.unofficial && (nom.ceremony === this.ceremony) );
      }));
    },

    ceremonyFilmsViewed() {
      return this.filmList.reduce((accNumViewed, curFilm) => {
        return accNumViewed + (userStore.isFilmMarkedViewed(curFilm._id) ? 1 : 0);
      }, 0);
    },

    officiallyNominatedFilmsViewed() {
      return this.officiallyNominatedFilms.reduce((accNumViewed, curFilm) => {
        return accNumViewed + (userStore.isFilmMarkedViewed(curFilm._id) ? 1 : 0);
      }, 0);
    },

    percentageViewed() {
      return ((this.ceremonyFilmsViewed / this.filmList.length) * 100).toFixed();
    },

    officiallyNominatedPercentageViewed() {
      return ((this.officiallyNominatedFilmsViewed / this.officiallyNominatedFilms.length) * 100).toFixed();
    },

    ceremonyReviewAchievementImage() {

      return (
          userStore.isCeremonyReviewed(this.ceremony) ?
              'achievements/checklist-and-magnifier.svg' :
              'achievements/disabled/checklist-and-magnifier.svg'
      );
    },

    shouldDisplayFilmPercentageFootnote() {
      return this.filmList.some(film => film.nominations.some(nom => {
        return ( nom.unofficial && (nom.ceremony === this.ceremony) );
      }));
    }
  },
  methods: {

    installDebugHooks() {
      window.debugScrollCeremonyNavbar = (pixels, pixelsPerSecond, easeInEaseOut=false) => {

        const start = window.pageXOffset;
        let startTime = null;

        const durationMs = (pixels / pixelsPerSecond) * 1000;
        //console.log('speed', pixelsPerSecond);

        const easeInOutSine = t => -(Math.cos(Math.PI * t) - 1) / 2;

        const animation = currentTime => {
          if (startTime === null) startTime = currentTime;
          const timeElapsed = currentTime - startTime;
          const progress = easeInEaseOut? easeInOutSine(Math.min(timeElapsed / durationMs, 1)) : Math.min(timeElapsed / durationMs, 1);

          const distanceCovered = pixels * progress;
          this.$refs.ceremonynavbar.scrollTo({
            top: 0,
            left: start + distanceCovered,
            behavior: 'instant'
          });

          if ( timeElapsed < durationMs ) {
            requestAnimationFrame(animation);
          }
        };
        requestAnimationFrame(animation);
      }
    },

    async filmViewedStateUpdated(_film_id) {
      if ( this.officiallyNominatedFilms.some(f => f._id === _film_id) && userStore.isFilmMarkedViewed(_film_id) ) {

        // We do the pulse on the next anim because it can be wiped out by some vue reactions
        //  (like removing the no-films-viewed class when the first box is checked).
        return nextTick().then(() => {
            if ( this.$refs.floatingPercentageStatRef ) {
              this.$refs.floatingPercentageStatRef?.classList.remove('percent-stat-pulse');
              void this.$refs.floatingPercentageStatRef.offsetWidth;
              this.$refs.floatingPercentageStatRef?.classList.add('percent-stat-pulse');
            }

            if ( this.$refs.percentageStatRef ) {
              this.$refs.percentageStatRef?.classList.remove('percent-stat-pulse');
              void this.$refs.percentageStatRef.offsetWidth;
              this.$refs.percentageStatRef?.classList.add('percent-stat-pulse');
            }
          });
      }
    },

    async setCeremony(_ceremony) {
      //console.log(`setCeremony(${_ceremony})`)

      if ( (_ceremony > 0) && (_ceremony <= this.numCeremonies) && (_ceremony != this.ceremony) ) {

        analytics.sendPage(this.$options.name, {
          ceremony: _ceremony
        });

        this.ceremony = _ceremony;
        this.filmList = [];

        axios.get(`/api/ceremony/${_ceremony}/films`)
            .then((response) => {
              this.filmList = response.data.map(f => new Film(f));

              this.filmsReviewed = new Set();
              Film.sortFilmsByPrioirity(this.filmList);

              if ( !this.CEREMONY_REVIEWING_ENABLED || userStore.isCeremonyReviewed(_ceremony) ) {
                //console.log(`ceremony ${_ceremony} reviewed`)
                this.filmsReviewed = new Set(this.filmList);

              } else {
                //console.log(`ceremony ${_ceremony} not reviewed`)
                this.filmsReviewed = new Set();
              }

            })

            .catch((err) => {
              analytics.notifyBugsnagOfError(err);
            });
      }
    },

    ceremonyYearsHonoredText(_ceremony) {
      return Film.ceremonyYearsHonoredText(_ceremony);
    },

    getCeremonyOrdinalText(_ceremony) {
      return Film.getCeremonyOrdinalText(_ceremony);
    },

    getYearOfCeremony(_ceremony) {
      const date = Film.getDateOfCeremony(_ceremony);
      return date.getUTCFullYear();
    },

    isDecadeEdge(_ceremony) {
      // don't designate ceremony edges before the 7th one
      if ( _ceremony < 7 ) {
        return false;
      }
      const ceremonyYear = this.getYearOfCeremony(_ceremony);
      return ( ceremonyYear === (Math.floor(ceremonyYear / 10) * 10) );
    },

    // eslint-disable-next-line no-unused-vars
    showNeedsReviewIndicator(_ceremony) {
      return !this.$screenshotMode && !userStore.isCeremonyReviewed(_ceremony);
    },

    onScroll(/*e*/) {
      this.handleCeremonyOverlay();
    },

    handleCeremonyOverlay() {
      if ( this.$screenshotMode ) {
        if ( this.$screenshotModeSettings.hideChecklistCeremonyOverlay) {
          this.showCeremonyOverlay = false;
        } else {
          this.showCeremonyOverlay = (window.top.scrollY > 300);
        }
        return;
      }

      this.showCeremonyOverlay = (window.top.scrollY > 100);
    },

    findFilmInFilmlist(_filmId) {
      return this.filmList.find(film => {
        return film._id === _filmId;
      });
    },

    filmsNominatedForCategory(_category) {
      return Film.filterFilmsNominatedForCategory(this.filmList, _category, this.ceremony).sort((filmA, filmB) => {
        const nomsA = filmA.nominationsForCategory(_category);
        const nomsB = filmB.nominationsForCategory(_category);

        // looking for ANY winning nom
        const hasWinA = nomsA.some(n => n.won);
        const hasWinB = nomsB.some(n => n.won);

        if (hasWinA && !hasWinB) {
          return -1;
        }
        if (hasWinB && !hasWinA) {
          return 1;
        }

        // looking for NO official nom (thus the double-negation)
        const allUnofficialA = !nomsA.some(n => !n.unofficial);
        const allUnofficialB = !nomsB.some(n => !n.unofficial);

        if (allUnofficialA && !allUnofficialB) {
          return 1;
        }
        if (allUnofficialB && !allUnofficialA) {
          return -1;
        }
        return 0;
      });
    },

    categoryDisplayNameHtml(_categoryName) {
      return Film.categoryDisplayNameHtml(_categoryName);
    },

  }
}
</script>


<style scoped>

* {
  --ceremony-navbar-content-height: 46px;
  --ceremony-navbar-padding-height: 10px;
  --alloted-ceremony-navbar-height: calc(var(--ceremony-navbar-content-height) + var(--ceremony-navbar-padding-height) + var(--ceremony-navbar-padding-height) + var(--mol-scrollbar-width));
}

.checklist-container {

  padding-top: calc(12px + var(--alloted-ceremony-navbar-height));
  padding-bottom: 12px;

  &.showing-message-banner {
    padding-top: calc(12px + var(--alloted-ceremony-navbar-height) + var(--mol-membership-banner-bar-height));
  }
}

.ceremony-navbar {
  /*opacity: 20%;*/

  /* set top to avoid navbar, and possibly the message banner */
  top: var(--mol-navbar-height);
  &.showing-message-banner {
    top: calc(var(--mol-navbar-height) + var(--mol-membership-banner-bar-height));
  }

  display: flex;
  flex-wrap: nowrap;
  padding: var(--ceremony-navbar-padding-height) 0 var(--ceremony-navbar-padding-height) 0;
  margin-block: 0;
  overflow-x: auto;
  list-style: none;
  text-align: center;
  white-space: nowrap;

  z-index: 20;
  overflow-y: hidden;
  scroll-behavior: smooth;

  .nav-item {
    &.decade-edge {
      border-style: none solid none none;
      border-width: 1px;
    }
    line-height: 15px;
    position: relative;

    &.active {
      font-weight: 700;
    }

    .nav-ceremony-num {
    }
    .nav-year {
      font-size: small;
    }
    .active {
      font-weight: 700;
    }
    a {
      color: inherit;
    }
    .ceremony-needs-review-dot {
      top: 0;
      position: absolute;
      color: var(--mol-color-gold);
    }
  }
}

/* ceremony beneath navbar */
.ceremony-card {
  font-size: large;

  .percentage-stat {
    text-align: center;
    font-family: 'Playpen Sans', cursive, sans-serif;
    color: var(--mol-color-gold-text);

    &.no-films-viewed {
      color: var(--bs-tertiary-color);
    }
  }

  /* floating header that appears when the ceremony year is scrolled out of view */
  .floating-ceremony-header {

    --floating-ceremony-header-local-margin-top: 10px;

    /* set top to avoid navbar, and possibly the message banner */
    top: calc(var(--floating-ceremony-header-local-margin-top) + var(--mol-navbar-height) + var(--alloted-ceremony-navbar-height));

    &.showing-message-banner {
      top: calc(var(--floating-ceremony-header-local-margin-top) + var(--mol-navbar-height) + var(--alloted-ceremony-navbar-height) + var(--mol-membership-banner-bar-height));
    }

    margin-left: auto;
    margin-right: auto;
    width: fit-content;

    font-size: 14px;

    .percentage-stat {
      font-size: 12px;
    }

    background-color: var(--bs-body-bg);


    z-index: 4;
    box-shadow: 0px 0px 50px 1px var(--bs-border-color);

    border-radius: 10px;
    border-color: var(--bs-border-color);
    border-style: solid;
    border-width: thin;
    padding: 5px 15px 5px 15px;

    /*background-color: RGB(248, 249, 250);*/
    animation: fadeIn 0.25s;

    .ceremony-text {
      display: inline-block;
      font-weight: bold;
      padding: 0 0 0 0.3em;
    }

    .ceremony-needs-review-dot {
      top: 0;
      position: absolute;
      color: var(--mol-color-gold);
    }

    &.floating-ceremony-header-screenshot-mode {

      top: 70px;
      font-size: 35px;

      .percentage-stat {
        font-size: 35px;
      }
    }

  }


  .ceremony-card-header {

    /*
    position: sticky;
    z-index: 10;
    top: 120px;
     */

    background-color: inherit;

    text-align: center;

    border-style: none none solid none;
    border-color: var(--bs-card-border-color);
    border-width: 1px;

    width: 90%;
    font-size: smaller;

    margin: 1em auto 0 auto;
    padding: 1em 0 0 0;

    .ceremony-number-and-year {
      font-weight: bold;
      font-size: large;
    }

    .group-by-nomination {

      font-size: xx-small;
      margin-top: 35px;

      width: fit-content;
      margin-left: auto;
      margin-right: auto;

      .group-by-nomination-switch {
        text-align: left;
        margin: 0;

        .form-check-label {
          &:hover {
            color: var(--bs-link-hover-color);
          }
        }
      }
    }

    .ceremony-needs-review-dot {
      top: 1.125em;
      position: absolute;
      color: var(--mol-color-gold);
    }

  }
}

.card-body {
  width: 100%;
  max-width: 500px;
  margin-right: auto;
  margin-left: auto;

  hr {
    color: var(--bs-card-border-color);
    margin-top: 0;
    margin-bottom: 0;
  }
}

.card-body-film-list {

}

.card-body-category-list {
  margin-top: 0;
  padding-top: 0;

  .category-title {
    text-align: center;
    font-size: x-large;
    padding: 1em 1em 0.5em 1em;
    margin-top: 1.5em;
    border-style: solid none none none;
    border-color: var(--bs-card-border-color);
    border-width: 1px;

    /* top category has a smaller top margin */
    &.first-category {
      padding-top: 1em;
      margin-top: 0;
      border-style: none;
    }
  }
}

:deep(.logo-image) {
  height: 1.1em;
  vertical-align: text-bottom;
  margin-right: 0.1em;
}


@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.percent-stat-pulse {
  animation: pulse-animation 0.4s ease-out;
}

@keyframes pulse-animation {
  0%   { transform: scale(1); filter: brightness(1); }
  30%  { transform: scale(1.28); filter: brightness(1.15); }
  60%  { transform: scale(0.97); filter: brightness(1.05); } /* subtle dip */
  100% { transform: scale(1); filter: brightness(1); }
}


.film-percentage-footnote {
  padding: 1em;
  font-size: xx-small;
  text-align: right;
  font-style: italic;
  color: var(--mol-color-gold-text);


}

</style>